import React, { useState, useEffect } from "react";
import { toast } from "sonner";
import { X, Send, AlertCircle, CheckCircle2, Loader2, BellRing } from "lucide-react";
import { useApi } from "../../hooks/useApi";
import { formatCurrency } from "../../utils/formatters";
import { COLORS } from "../../utils/constants";
import { UrssafConnectLogo, UrssafDisclaimerDeclaration } from "./UrssafBrandingKit";

/**
 * Modale de déclaration Urssaf "1 clic" — 100% auto :
 * - Charge GET /api/urssaf/declarer/preview au montage (période, CA, cotisations)
 * - Affiche le récap en lecture seule (aucune saisie)
 * - Confirmation → POST /api/urssaf/declarer/auto
 * - Prend en charge : mensuel/trimestriel, ACRE, VL, double activité
 */
export const UrssafDeclareModal = ({ open, onClose, onSuccess, dashboardSnapshot }) => {
  const api = useApi();
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [payerAuto, setPayerAuto] = useState(false);
  const [result, setResult] = useState(null);
  const [reminderSet, setReminderSet] = useState(false);
  const [reminderLoading, setReminderLoading] = useState(false);
  const [rectifierMode, setRectifierMode] = useState(false);
  const [sepaActif, setSepaActif] = useState(true);
  const [retryingPay, setRetryingPay] = useState(false);

  useEffect(() => {
    if (!open) return;
    setResult(null);
    setReminderSet(false);
    setRectifierMode(false);
    setLoading(true);
    api.get("/urssaf/status")
      .then((r) => { const ok = (r.data?.sepa_mandats_actifs || 0) > 0; setSepaActif(ok); if (!ok) setPayerAuto(false); })
      .catch(() => {});
    api.get("/urssaf/declarer/preview")
      .then((r) => setPreview(r.data))
      .catch(() => toast.error("Impossible de charger l'aperçu de déclaration"))
      .finally(() => setLoading(false));
  }, [open]);

  if (!open) return null;

  const dejaDec = preview?.deja_declaree;
  const estim = preview?.estimation;
  const breakdown = preview?.breakdown;
  const periode = preview?.periode;
  const statutOuv = periode?.statut_ouverture;
  const periodeOuverte = statutOuv ? statutOuv.ouverte !== false : true;
  const periodeStatut = statutOuv?.statut; // "future" | "cloturee" | "ouverte"
  const retryPayment = async () => {
    setRetryingPay(true);
    try {
      const r = await api.post(`/urssaf/declarations/${result.data.declaration.id}/payer`);
      setResult((prev) => ({
        ...prev,
        data: {
          ...prev.data,
          paiement_erreur: null,
          declaration: { ...prev.data.declaration, paiement_effectue: true, reference_paiement: r.data.reference_paiement },
        },
      }));
      toast.success("Paiement programmé");
    } catch (e) {
      const d = e.response?.data?.detail;
      const msg = typeof d === "string" ? d : d?.message || "Paiement impossible";
      setResult((prev) => ({ ...prev, data: { ...prev.data, paiement_erreur: msg } }));
    }
    setRetryingPay(false);
  };

  const periodiciteMismatch = preview?.periodicite_mismatch;
  const payload = preview?.payload;

  // Formate une date ISO YYYY-MM-DD en JJ/MM/AAAA
  const fmtDate = (iso) => {
    if (!iso) return "";
    const [y, m, d] = iso.split("-");
    return `${d}/${m}/${y}`;
  };

  // Override CA from dashboard snapshot if available (source de vérité = encart dashboard)
  const totalCaFromSnapshot = dashboardSnapshot
    ? (parseFloat(dashboardSnapshot.montant_a_declarer_principal || 0) +
       parseFloat(dashboardSnapshot.montant_a_declarer_secondaire || 0))
    : null;
  const displayCaTotal = totalCaFromSnapshot ?? (estim?.ca_total || 0);
  const caPrincipalDisplay = dashboardSnapshot
    ? parseFloat(dashboardSnapshot.montant_a_declarer_principal || 0)
    : (breakdown?.activite_principale?.montant || 0);
  const caSecondaireDisplay = dashboardSnapshot
    ? parseFloat(dashboardSnapshot.montant_a_declarer_secondaire || 0)
    : (breakdown?.activite_secondaire?.montant || 0);
  const cotisationsTotal = dashboardSnapshot
    ? parseFloat(dashboardSnapshot.prelevement_estime || 0)
    : (estim?.total_a_payer || 0);

  // Fix incohérence : recalculer cotisations sociales & CFP au prorata du CA affiché
  // (le backend calcule sur un autre périmètre — cumul période/année — ce qui crée un delta)
  const scaleRatio = (estim?.ca_total && estim.ca_total > 0)
    ? (displayCaTotal / estim.ca_total)
    : 1;
  const cotisationsSocialesDisplay = (estim?.cotisations_sociales || 0) * scaleRatio;
  const cfpDisplay = (estim?.cfp || 0) * scaleRatio;
  const versementLibDisplay = (estim?.versement_liberatoire || 0) * scaleRatio;

  const programmerRappel = async () => {
    setReminderLoading(true);
    try {
      await api.post("/urssaf/reminder", {});
      setReminderSet(true);
      toast.success(`Rappel programmé pour le ${fmtDate(statutOuv?.date_ouverture)}`);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : (detail?.message || "Impossible de programmer le rappel"));
    }
    setReminderLoading(false);
  };

  const submit = async () => {
    setSubmitting(true);
    try {
      const body = dashboardSnapshot ? { dashboard_snapshot: dashboardSnapshot } : {};
      const url = rectifierMode
        ? `/urssaf/declarer/auto/rectifier?payer_automatiquement=${payerAuto}`
        : `/urssaf/declarer/auto?payer_automatiquement=${payerAuto}`;
      const r = await api.post(url, body);
      setResult({ ok: true, data: r.data, rectificative: rectifierMode });
      toast.success(
        rectifierMode
          ? `Rectification transmise à l'Urssaf : ${r.data.declaration.numero_declaration}`
          : `Déclaration transmise à l'Urssaf : ${r.data.declaration.numero_declaration}`
      );
      onSuccess?.(r.data);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      let msg = detail?.message || detail || "Erreur";
      const code = detail?.code;
      if (typeof msg !== "string") msg = "Erreur déclaration";
      const looksLikeHtml = /<[a-z][\s\S]*>/i.test(msg) || /cloudflare|origin web server/i.test(msg);
      if (looksLikeHtml || code === "ERR_URSSAF_INDISPONIBLE") {
        msg = "L'Urssaf est temporairement indisponible. Réessaie dans quelques minutes — ta déclaration n'a pas été envoyée, aucun risque de doublon.";
      }
      setResult({ ok: false, error: msg, code });
      toast.error(code === "ERR_URSSAF_INDISPONIBLE" ? "Urssaf indisponible" : msg.slice(0, 120));
    }
    setSubmitting(false);
  };

  const activePrincipal = breakdown?.activite_principale;  // eslint-disable-line no-unused-vars
  const activeSecondaire = breakdown?.activite_secondaire;  // eslint-disable-line no-unused-vars

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" data-testid="urssaf-declare-modal">
      <div className="bg-white rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b" style={{backgroundColor: COLORS.bg}}>
          <div className="flex items-center gap-3">
            <UrssafConnectLogo size={40} />
            <div>
              <h2 className="text-xl font-bold" style={{color: COLORS.text}}>Déclarer mon chiffre d'affaires à l'Urssaf</h2>
              <p className="text-sm text-muted-foreground">
                {periode?.label ? `Période : ${periode.label}` : "Chargement…"}
              </p>
            </div>
          </div>
          <button onClick={onClose} className="hover:opacity-70" data-testid="urssaf-declare-close">
            <X size={20} />
          </button>
        </div>

        <div className="p-6 space-y-4">
          {loading && (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="animate-spin" size={32} style={{color: COLORS.accent}} />
            </div>
          )}

          {!loading && dejaDec && !rectifierMode && !result && (
            <div className="space-y-3">
              <div className="p-4 rounded-lg text-center space-y-2" style={{backgroundColor: COLORS.success + "15", border: `2px solid ${COLORS.success}`}}>
                <CheckCircle2 size={32} className="mx-auto" style={{color: COLORS.success}} />
                <div className="font-bold" style={{color: COLORS.success}}>Déclaration transmise à l'Urssaf</div>
                <div className="text-sm text-muted-foreground">
                  N° {dejaDec.numero_declaration} · Montant {formatCurrency(dejaDec.montant_a_payer)}
                </div>
                <div className="text-xs">
                  {dejaDec.paiement_effectue ? (
                    <span className="font-semibold" style={{color: COLORS.success}}>
                      ✓ Prélèvement SEPA effectué{dejaDec.date_prelevement ? ` le ${fmtDate(dejaDec.date_prelevement)}` : ""}
                    </span>
                  ) : dejaDec.date_prelevement ? (
                    <span style={{color: COLORS.accent}}>
                      Prélèvement SEPA programmé le <strong>{fmtDate(dejaDec.date_prelevement)}</strong>
                    </span>
                  ) : (
                    <span className="text-muted-foreground">
                      Reçue par l'Urssaf — prélèvement SEPA à venir
                    </span>
                  )}
                </div>
              </div>
              {periodeOuverte ? (
                <>
                  <div className="p-3 rounded-lg flex items-start gap-2 text-xs" style={{backgroundColor: "#FEF3C7", color: "#92400E"}}>
                    <AlertCircle size={14} className="mt-0.5 flex-shrink-0" />
                    <p>
                      Oublié une vente&nbsp;? Tu peux envoyer une déclaration <strong>rectificative</strong> jusqu'au{" "}
                      <strong>{fmtDate(statutOuv?.date_limite)}</strong>. Elle annule et remplace la précédente côté
                      Urssaf (visible sur ton espace autoentrepreneur.urssaf.fr), et si tu as un mandat SEPA, le
                      prélèvement est ajusté au nouveau montant.
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() => setRectifierMode(true)}
                    className="w-full py-2 rounded-lg font-medium border transition-colors"
                    style={{ borderColor: COLORS.accent, color: COLORS.accent, backgroundColor: "white" }}
                    data-testid="urssaf-rectifier-btn"
                  >
                    Modifier / Renvoyer la déclaration
                  </button>
                </>
              ) : (
                <div className="p-3 rounded-lg flex items-start gap-2 text-xs" style={{backgroundColor: "#FEE2E2", color: "#991B1B"}}>
                  <AlertCircle size={14} className="mt-0.5 flex-shrink-0" />
                  <p>
                    La période <strong>{periode?.label}</strong> est clôturée depuis le{" "}
                    <strong>{fmtDate(statutOuv?.date_limite)}</strong>. La déclaration est verrouillée côté Urssaf&nbsp;:
                    aucune rectification n'est possible via l'app. Rends-toi sur ton espace Urssaf pour une régularisation.{" "}
                    <a href="https://www.autoentrepreneur.urssaf.fr" target="_blank" rel="noopener noreferrer" className="underline font-semibold" data-testid="urssaf-cloturee-link">autoentrepreneur.urssaf.fr</a>
                  </p>
                </div>
              )}
            </div>
          )}

          {!loading && (!dejaDec || rectifierMode) && preview && !result && !periodeOuverte && (
            <div
              className="p-4 rounded-lg space-y-3"
              style={{
                backgroundColor: COLORS.bg,
                border: `2px solid ${periodeStatut === "cloturee" ? COLORS.error : COLORS.accent}`,
              }}
              data-testid="urssaf-periode-fermee"
            >
              <div className="flex items-center gap-2 font-bold" style={{ color: periodeStatut === "cloturee" ? COLORS.error : COLORS.accent }}>
                <AlertCircle size={18} />
                {periodeStatut === "future"
                  ? "Période pas encore ouverte à la déclaration"
                  : "Période clôturée"}
              </div>
              <div className="text-sm" style={{ color: COLORS.text }}>
                {periodeStatut === "future" ? (
                  <>
                    La période <strong>{periode?.label}</strong> ne sera ouverte à la déclaration Urssaf
                    qu'à partir du <strong>{fmtDate(statutOuv?.date_ouverture)}</strong> (date limite : {fmtDate(statutOuv?.date_limite)}).
                    Reviens à cette date : Biz360 déclarera ton CA en 1 clic.
                  </>
                ) : (
                  <>
                    La date limite pour déclarer <strong>{periode?.label}</strong> était le{" "}
                    <strong>{fmtDate(statutOuv?.date_limite)}</strong>. Contacte l'Urssaf pour régulariser
                    ta situation (une pénalité peut s'appliquer).
                  </>
                )}
              </div>
              {periodeStatut === "future" && (
                <button
                  type="button"
                  onClick={programmerRappel}
                  disabled={reminderLoading || reminderSet}
                  className="inline-flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-opacity"
                  style={{
                    backgroundColor: reminderSet ? COLORS.success : COLORS.accent,
                    color: "white",
                    opacity: (reminderLoading || reminderSet) ? 0.85 : 1,
                    cursor: reminderLoading ? "wait" : (reminderSet ? "default" : "pointer"),
                  }}
                  data-testid="urssaf-reminder-btn"
                >
                  {reminderSet ? <><CheckCircle2 size={14} /> Rappel programmé — on t'écrit le {fmtDate(statutOuv?.date_ouverture)}</>
                    : <><BellRing size={14} /> Me rappeler le {fmtDate(statutOuv?.date_ouverture)}</>}
                </button>
              )}
            </div>
          )}

          {!loading && (!dejaDec || rectifierMode) && preview && !result && periodiciteMismatch && (
            <div
              className="p-4 rounded-lg space-y-1"
              style={{ backgroundColor: COLORS.bg, border: `2px solid ${COLORS.error}` }}
              data-testid="urssaf-periodicite-mismatch"
            >
              <div className="flex items-center gap-2 font-bold" style={{ color: COLORS.error }}>
                <AlertCircle size={18} />
                Périodicité incohérente avec l'Urssaf
              </div>
              <div className="text-sm" style={{ color: COLORS.text }}>{periodiciteMismatch.message}</div>
            </div>
          )}

          {!loading && (!dejaDec || rectifierMode) && preview && !result && (
            <>
              {rectifierMode && dejaDec && (
                <div className="p-3 rounded-lg flex items-start gap-2 text-xs" style={{backgroundColor: COLORS.accent + "15", color: COLORS.accent, border: `1px dashed ${COLORS.accent}`}}>
                  <AlertCircle size={14} className="mt-0.5 flex-shrink-0" />
                  <p>
                    <strong>Rectification en cours</strong> — tu renvoies la déclaration pour {periode?.label}.
                    Elle remplacera la précédente (n° {dejaDec.numero_declaration}) côté Urssaf.
                  </p>
                </div>
              )}
              {/* Récap des CA par activité (source : encart dashboard) */}
              <div>
                <div className="text-sm font-medium mb-2" style={{color: COLORS.text}}>
                  Chiffre d'affaires encaissé (source : ton tableau de bord)
                </div>
                <div className="rounded-lg border overflow-hidden" style={{borderColor: "#e5e7eb"}}>
                  {breakdown?.activite_principale && (
                    <div className="flex justify-between items-center p-3 border-b" style={{borderColor: "#f3f4f6"}}>
                      <div>
                        <div className="text-sm font-medium" style={{color: COLORS.principale}}>
                          Activité principale
                        </div>
                        <div className="text-xs text-muted-foreground capitalize">{breakdown.activite_principale.label}</div>
                      </div>
                      <div className="text-lg font-bold" style={{color: COLORS.text}}>
                        {formatCurrency(caPrincipalDisplay)}
                      </div>
                    </div>
                  )}
                  {breakdown?.activite_secondaire && (
                    <div className="flex justify-between items-center p-3 border-b" style={{borderColor: "#f3f4f6"}}>
                      <div>
                        <div className="text-sm font-medium" style={{color: COLORS.accent}}>
                          Activité secondaire
                        </div>
                        <div className="text-xs text-muted-foreground capitalize">{breakdown.activite_secondaire.label}</div>
                      </div>
                      <div className="text-lg font-bold" style={{color: COLORS.text}}>
                        {formatCurrency(caSecondaireDisplay)}
                      </div>
                    </div>
                  )}
                  <div className="flex justify-between items-center p-3 font-bold" style={{backgroundColor: COLORS.bg}}>
                    <span style={{color: COLORS.text}}>CA total à déclarer</span>
                    <span className="text-xl" style={{color: COLORS.accent}} data-testid="urssaf-total-ca">
                      {formatCurrency(displayCaTotal)}
                    </span>
                  </div>
                </div>
              </div>

              {/* Options actives (ACRE + VL) */}
              <div className="flex flex-wrap gap-2">
                {breakdown?.exo_acre_applique && (
                  <span className="px-3 py-1 rounded-full text-xs font-medium" style={{backgroundColor: COLORS.success + "20", color: COLORS.success}}>
                    ✓ ACRE appliquée (-50%)
                  </span>
                )}
                {breakdown?.versement_liberatoire && (
                  <span className="px-3 py-1 rounded-full text-xs font-medium" style={{backgroundColor: COLORS.accent + "20", color: COLORS.accent}}>
                    ✓ Versement libératoire
                  </span>
                )}
                <span className="px-3 py-1 rounded-full text-xs font-medium" style={{backgroundColor: "#EEF2FF", color: "#3730A3"}}>
                  {periode?.periodicite === "M" ? "Déclaration mensuelle" : "Déclaration trimestrielle"}
                </span>
              </div>

              {/* Estimation cotisations */}
              {estim && (
                <div className="p-4 rounded-lg space-y-2" style={{backgroundColor: COLORS.accent + "10", border: `2px solid ${COLORS.accent}`}}>
                  <div className="text-sm font-medium" style={{color: COLORS.accent}}>Détail des cotisations</div>
                  <div className="flex justify-between text-sm">
                    <span>Cotisations sociales</span>
                    <span data-testid="urssaf-cotis-sociales">{formatCurrency(cotisationsSocialesDisplay)}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span>CFP (Formation Pro)</span>
                    <span>{formatCurrency(cfpDisplay)}</span>
                  </div>
                  {versementLibDisplay > 0 && (
                    <div className="flex justify-between text-sm">
                      <span>Versement libératoire</span>
                      <span>{formatCurrency(versementLibDisplay)}</span>
                    </div>
                  )}
                  <hr className="border-dashed" />
                  <div className="flex justify-between font-bold text-lg">
                    <span>Total à payer</span>
                    <span style={{color: COLORS.accent}} data-testid="urssaf-total-a-payer">
                      {formatCurrency(cotisationsTotal)}
                    </span>
                  </div>
                </div>
              )}

              {/* Option paiement auto */}
              <label
                className={`flex items-start gap-2 p-3 rounded-lg ${sepaActif ? "cursor-pointer" : "cursor-not-allowed opacity-60"}`}
                style={{backgroundColor: COLORS.bg}}
                title={sepaActif ? undefined : "Aucun mandat SEPA actif : crée-le sur ton espace Urssaf, puis clique « Rafraîchir » dans Mes Paramètres"}
              >
                <input
                  type="checkbox"
                  checked={payerAuto}
                  disabled={!sepaActif}
                  onChange={(e) => setPayerAuto(e.target.checked)}
                  className="mt-1"
                  data-testid="urssaf-payer-auto"
                />
                <div className="text-sm">
                  <div className="font-medium">Payer automatiquement via mon mandat SEPA</div>
                  <div className="text-xs text-muted-foreground">
                    {sepaActif ? "Le prélèvement est programmé dès l'envoi de la déclaration" : "Indisponible : aucun mandat SEPA actif (à créer sur ton espace Urssaf)"}
                  </div>
                </div>
              </label>

              <div className="p-3 rounded-lg flex items-start gap-2 text-xs" style={{backgroundColor: "#FEF3C7", color: "#92400E"}}>
                <AlertCircle size={14} className="mt-0.5 flex-shrink-0" />
                <p>
                  En confirmant, tu autorises Biz360 à transmettre cette déclaration à l'Urssaf en ton nom.
                  Ces chiffres sont calculés automatiquement depuis tes ventes payées de la période.
                </p>
              </div>

              {/* Disclaimer OBLIGATOIRE kit éditorial ACOSS - écran de validation déclaration */}
              <UrssafDisclaimerDeclaration />
            </>
          )}

          {/* Écran de succès / échec après envoi */}
          {!loading && result?.ok && (
            <div className="p-5 rounded-xl text-center space-y-3" style={{backgroundColor: COLORS.success + "15", border: `2px solid ${COLORS.success}`}} data-testid="urssaf-result-success">
              <CheckCircle2 size={44} className="mx-auto" style={{color: COLORS.success}} />
              <div>
                <div className="text-lg font-bold" style={{color: COLORS.success}}>
                  {result.rectificative ? "Rectification transmise à l'Urssaf ✓" : "Déclaration transmise à l'Urssaf ✓"}
                </div>
                <p className="text-sm text-muted-foreground mt-1">
                  N° de déclaration : <strong>{result.data.declaration.numero_declaration}</strong>
                </p>
                <p className="text-sm text-muted-foreground">
                  Montant : <strong>{formatCurrency(result.data.declaration.montant_a_payer)}</strong>
                  {result.data.declaration.date_prelevement && <> · Prélèvement : {result.data.declaration.date_prelevement}</>}
                </p>
                {result.data.declaration.paiement_effectue && (
                  <p className="text-sm mt-2 font-medium" style={{color: COLORS.success}} data-testid="urssaf-result-paiement">
                    Paiement de {formatCurrency(result.data.declaration.montant_a_payer)} programmé via ton mandat SEPA
                    {result.data.declaration.date_prelevement ? ` pour le ${result.data.declaration.date_prelevement}` : ""}
                    {result.data.declaration.reference_paiement ? ` — réf. ${result.data.declaration.reference_paiement}` : ""}
                  </p>
                )}
                {result.data.paiement_erreur && (
                  <div className="text-xs mt-3 p-3 rounded-lg text-left" style={{backgroundColor: "#FEF3C7", color: "#92400E"}} data-testid="urssaf-result-paiement-erreur">
                    <strong>Déclaration bien reçue, mais le paiement n'a pas pu être programmé</strong> ({result.data.paiement_erreur}).
                    Ta déclaration reste valide. Tu peux retenter le paiement ou régler tes cotisations sur{" "}
                    <a href="https://www.autoentrepreneur.urssaf.fr" target="_blank" rel="noopener noreferrer" className="underline font-semibold">ton espace Urssaf</a>.
                    <button
                      type="button"
                      onClick={retryPayment}
                      disabled={retryingPay}
                      className="mt-2 w-full h-9 rounded-lg text-xs font-semibold text-white inline-flex items-center justify-center gap-2 disabled:opacity-50"
                      style={{backgroundColor: "#92400E"}}
                      data-testid="urssaf-retry-paiement-btn"
                    >
                      {retryingPay && <Loader2 size={12} className="animate-spin" />} Retenter le paiement
                    </button>
                  </div>
                )}
                {result.rectificative && result.data.declaration_precedente && (
                  <p className="text-xs text-muted-foreground mt-2">
                    Remplace la déclaration n° <strong>{result.data.declaration_precedente.numero_declaration}</strong>{" "}
                    ({formatCurrency(result.data.declaration_precedente.montant_a_payer)})
                  </p>
                )}
              </div>
              {result.data.environnement === "mock" && (
                <div className="text-xs px-3 py-2 rounded-lg inline-block" style={{backgroundColor: "#FEF3C7", color: "#92400E"}}>
                  ℹ️ Environnement de test (sandbox / mock) — pas d'envoi vers l'Urssaf de production
                </div>
              )}
            </div>
          )}
          {!loading && result && !result.ok && (
            <div className="p-5 rounded-xl text-center space-y-2" style={{backgroundColor: "#FEE2E2", border: "2px solid #DC2626"}} data-testid="urssaf-result-error">
              <AlertCircle size={40} className="mx-auto" style={{color: "#DC2626"}} />
              <div className="font-bold" style={{color: "#DC2626"}}>La déclaration n'a pas été reçue par l'Urssaf</div>
              <p className="text-sm text-muted-foreground">{result.error}</p>
              <p className="text-xs text-muted-foreground">Réessaie dans quelques minutes ou contacte le support.</p>
            </div>
          )}
        </div>

        <div className="p-6 border-t flex gap-3">
          <button onClick={rectifierMode && !result ? () => setRectifierMode(false) : onClose} className="flex-1 py-2 rounded-lg border hover:bg-gray-50" data-testid="urssaf-declare-cancel">
            {rectifierMode && !result ? "Retour" : (dejaDec || result ? "Fermer" : "Annuler")}
          </button>
          {(!dejaDec || rectifierMode) && !result && (
            <button
              onClick={submit}
              disabled={submitting || loading || !preview || !periodeOuverte || !!periodiciteMismatch}
              className="flex-1 py-2 rounded-lg text-white font-medium flex items-center justify-center gap-2 transition-opacity"
              style={{
                backgroundColor: rectifierMode ? COLORS.accent : COLORS.success,
                opacity: (submitting || loading || !preview || !periodeOuverte || !!periodiciteMismatch) ? 0.5 : 1,
                cursor: (submitting || loading || !preview || !periodeOuverte || !!periodiciteMismatch) ? "not-allowed" : "pointer",
              }}
              data-testid="urssaf-declare-submit"
              title={
                periodiciteMismatch ? "Corrige d'abord la périodicité dans tes Paramètres" :
                !periodeOuverte ? (periodeStatut === "future"
                  ? `Ouverture le ${fmtDate(statutOuv?.date_ouverture)}`
                  : `Période clôturée depuis le ${fmtDate(statutOuv?.date_limite)}`) : undefined}
            >
              <CheckCircle2 size={18} />
              {submitting
                ? "Envoi…"
                : periodiciteMismatch
                  ? "Corrige la périodicité"
                  : !periodeOuverte
                    ? (periodeStatut === "future"
                        ? `Ouverture le ${fmtDate(statutOuv?.date_ouverture)}`
                        : "Période clôturée")
                    : rectifierMode
                      ? "Confirmer la rectification"
                      : (displayCaTotal <= 0
                          ? "Déclarer néant (CA 0 €)"
                          : "Confirmer et envoyer")}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};
